import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n/I18nProvider";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_KEYS = [
  "regular",
  "deep",
  "apartment",
  "office",
  "move_in_out",
  "post_construction",
];

function Field({ label, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <span className="text-xs uppercase tracking-widest text-zinc-400 font-medium mb-2 block">
        {label}
      </span>
      {children}
    </label>
  );
}

const inputCls =
  "w-full bg-black/60 border border-white/15 rounded-xl px-4 py-3 text-white placeholder:text-zinc-500 focus:border-[#EC008C] focus:ring-1 focus:ring-[#EC008C] outline-none transition-all text-sm";

export default function BookingForm() {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service_type: "regular",
    preferred_date: "",
    address: "",
    city: "",
    bedrooms: "2",
    bathrooms: "1",
    sqft: "",
    frequency: "one_time",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.address.trim()) {
      toast.error(lang === "es" ? "Completa nombre, teléfono y dirección." : "Please fill name, phone and address.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, language: lang, email: form.email || null };
      const { data } = await axios.post(`${API}/quotes`, payload);
      setResult(data);
      toast.success(t.booking.success);
    } catch (err) {
      toast.error(t.booking.error);
    } finally {
      setLoading(false);
    }
  };

  const serviceLabel = (key) => t.services.items.find((s) => s.key === key)?.title || key;

  if (result) {
    return (
      <section id="book" data-testid="booking-section" className="relative py-24 sm:py-32 overflow-hidden">
        <div className="glow-blob bg-[#EC008C] w-[400px] h-[400px] top-10 left-1/2 -translate-x-1/2 opacity-30" />
        <div className="max-w-2xl mx-auto px-5 lg:px-8 text-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            data-testid="booking-success"
            className="relative bg-[#0A0A0A] border border-[#EC008C] rounded-3xl p-10 lg:p-14 shadow-[0_0_60px_-10px_rgba(236,0,140,0.5)]"
          >
            <div className="mx-auto w-16 h-16 rounded-full bg-[#EC008C] flex items-center justify-center mb-6">
              <CheckCircle2 className="w-8 h-8 text-white" />
            </div>
            <h3 className="font-display text-3xl sm:text-4xl font-black tracking-tight">
              {lang === "es" ? "¡Solicitud recibida!" : "Request received!"}
            </h3>
            <p className="mt-4 text-zinc-400">
              {lang === "es"
                ? "Te contactaremos pronto a"
                : "We'll contact you soon at"}{" "}
              <span className="text-white font-semibold">{result.phone}</span>
            </p>
            <div className="mt-8 inline-flex items-center gap-3 px-6 py-4 rounded-2xl bg-[#EC008C]/10 border border-[#EC008C]/40">
              <Sparkles className="w-5 h-5 text-[#FF1F87]" />
              <div className="text-left">
                <div className="text-[10px] uppercase tracking-widest text-zinc-400">
                  {t.booking.estimated}
                </div>
                <div className="font-display text-2xl font-black pink-text">
                  {result.estimated_price}
                </div>
              </div>
            </div>
            <div className="mt-8">
              <button
                data-testid="booking-new"
                onClick={() => setResult(null)}
                className="text-sm text-zinc-300 hover:text-white underline underline-offset-4"
              >
                {lang === "es" ? "Enviar otra solicitud" : "Send another request"}
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="book" data-testid="booking-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="glow-blob bg-[#EC008C] w-[500px] h-[500px] -top-32 right-0 opacity-25" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#FF1F87]">
            {t.booking.eyebrow}
          </span>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-black tracking-tight">
            {t.booking.title}
          </h2>
          <p className="mt-5 text-base text-zinc-400">{t.booking.sub}</p>

          <div className="mt-10 space-y-3">
            {[
              { k: "1", t: t.how.steps[0].t, d: t.how.steps[0].d },
              { k: "2", t: t.how.steps[1].t, d: t.how.steps[1].d },
              { k: "3", t: t.how.steps[2].t, d: t.how.steps[2].d },
            ].map((s) => (
              <div key={s.k} className="flex gap-4 items-start">
                <div className="w-8 h-8 shrink-0 rounded-full bg-[#EC008C]/15 border border-[#EC008C]/40 flex items-center justify-center text-xs font-bold text-[#FF66B3]">
                  {s.k}
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">{s.t}</div>
                  <div className="text-xs text-zinc-400 mt-0.5">{s.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="booking-form"
          className="lg:col-span-7 bg-[#0A0A0A] border border-white/10 rounded-3xl p-7 lg:p-10"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label={t.booking.name} testId="field-name">
              <input
                data-testid="input-name"
                className={inputCls}
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Ana García"
              />
            </Field>
            <Field label={t.booking.phone} testId="field-phone">
              <input
                data-testid="input-phone"
                className={inputCls}
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="201-555-0100"
              />
            </Field>
            <Field label={t.booking.email} testId="field-email">
              <input
                data-testid="input-email"
                type="email"
                className={inputCls}
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder="email@dominio.com"
              />
            </Field>
            <Field label={t.booking.date} testId="field-date">
              <input
                data-testid="input-date"
                type="date"
                className={inputCls}
                value={form.preferred_date}
                onChange={(e) => set("preferred_date", e.target.value)}
              />
            </Field>
            <Field label={t.booking.service} testId="field-service">
              <select
                data-testid="input-service"
                className={inputCls}
                value={form.service_type}
                onChange={(e) => set("service_type", e.target.value)}
              >
                {SERVICE_KEYS.map((k) => (
                  <option key={k} value={k} className="bg-black">
                    {serviceLabel(k)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t.booking.frequency} testId="field-frequency">
              <select
                data-testid="input-frequency"
                className={inputCls}
                value={form.frequency}
                onChange={(e) => set("frequency", e.target.value)}
              >
                {Object.entries(t.booking.freqOptions).map(([k, v]) => (
                  <option key={k} value={k} className="bg-black">
                    {v}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t.booking.address} testId="field-address">
              <input
                data-testid="input-address"
                className={inputCls}
                value={form.address}
                onChange={(e) => set("address", e.target.value)}
                placeholder="123 Main St"
              />
            </Field>
            <Field label={t.booking.city} testId="field-city">
              <input
                data-testid="input-city"
                className={inputCls}
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
                placeholder="Jersey City"
              />
            </Field>
            <Field label={t.booking.bedrooms} testId="field-bedrooms">
              <input
                data-testid="input-bedrooms"
                type="number"
                min="0"
                max="10"
                className={inputCls}
                value={form.bedrooms}
                onChange={(e) => set("bedrooms", e.target.value)}
              />
            </Field>
            <Field label={t.booking.bathrooms} testId="field-bathrooms">
              <input
                data-testid="input-bathrooms"
                type="number"
                min="0"
                max="10"
                className={inputCls}
                value={form.bathrooms}
                onChange={(e) => set("bathrooms", e.target.value)}
              />
            </Field>
            <Field label={t.booking.sqft} testId="field-sqft">
              <input
                data-testid="input-sqft"
                className={inputCls}
                value={form.sqft}
                onChange={(e) => set("sqft", e.target.value)}
                placeholder="1200"
              />
            </Field>
          </div>

          <div className="mt-5">
            <Field label={t.booking.notes} testId="field-notes">
              <textarea
                data-testid="input-notes"
                rows={3}
                className={`${inputCls} resize-none`}
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                placeholder={lang === "es" ? "Mascotas, alergias, acceso..." : "Pets, allergies, access..."}
              />
            </Field>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="booking-submit"
            className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-[#EC008C] hover:bg-[#FF1F87] disabled:opacity-60 text-white px-7 py-4 rounded-full text-sm font-semibold transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_40px_rgba(236,0,140,0.45)]"
          >
            <Send className="w-4 h-4" />
            {loading ? t.booking.submitting : t.booking.submit}
          </button>
        </form>
      </div>
    </section>
  );
}
